import type {ChangeEvent} from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {P} from 'UI/components/layout/Text';

import {FadeInAnimation} from 'UI/animations/shared';
import {HintAnimation} from 'UI/animations/textInput';

import {useTextAreaInput} from './useTextAreaInput';

import type {ValidationFunc} from 'Shared/formHelperTypes';

/**
 * The `ComponentProps` interface defines the properties for the `TextAreaInput` component.
 * This interface establishes a comprehensive contract for all the configuration options available to customize the textarea input behavior, appearance, and validation.
 * It includes properties for form integration, accessibility features, styling customization, and user interaction handling while maintaining type safety throughout the component lifecycle.
 */
interface ComponentProps {
    /**
     * Optional CSS class name for custom styling of the wrapper element.
     * This property allows external styling systems to apply additional visual modifications to the component's root container.
     * When provided, the class name will be applied to the outermost wrapper div element of the TextAreaInput component.
     */
    className?: string;
    /**
     * Optional hint text displayed below the input field to provide additional guidance or information to the user.
     * This property is useful for providing contextual help, formatting instructions, or explanatory text that assists users in completing the input correctly.
     * The hint text appears below the textarea and is animated in/out based on the presence of validation errors.
     */
    hint?: string;
    /**
     * The initial value that will be displayed in the input field when the component first renders.
     * This property sets the default content of the textarea and serves as the starting point for user input or form data population.
     * If not provided, the textarea will start with an empty string as its initial value.
     */
    initialValue?: string;
    /**
     * Determines whether the input field is disabled, preventing any user interaction or input.
     * When set to true, the textarea becomes non-interactive, displays with a disabled visual state, and cannot receive focus or user input.
     * This is typically used when certain conditions are not met or when the form is in a processing state.
     *
     * @default false
     */
    isDisabled?: boolean;
    /**
     * Determines whether the input field is in readonly mode, allowing users to see the value but preventing modifications.
     * When set to true, the textarea displays its content but disables editing capabilities while maintaining focus and selection functionality.
     * This is useful for displaying data that should be visible but not editable in certain contexts or user states.
     *
     * @default false
     */
    isReadonly?: boolean;
    /**
     * Specifies whether the input field is required for form submission, typically displayed with an asterisk indicator.
     * When set to true, the component will add a visual required indicator (asterisk) to the label and set the HTML required attribute on the textarea element.
     * This property integrates with form validation systems to ensure users provide necessary information before form submission.
     *
     * @default false
     */
    isRequired?: boolean;
    /**
     * Optional label text that will be displayed above the input field to identify its purpose or expected content.
     * This property provides semantic meaning to the textarea by creating a proper label association for accessibility and user understanding.
     * When provided, the label is rendered above the textarea with appropriate spacing and includes required indicators when applicable.
     */
    label?: string;
    /**
     * The name attribute for the input field, which is essential for form data handling and submission processes.
     * This property serves as the unique identifier for the textarea within form contexts and is used for data collection, validation, and submission.
     * It must be provided as it's crucial for proper form functionality and accessibility standards.
     */
    name: string;
    /**
     * Optional callback function that is triggered whenever the input value changes, receiving the change event as a parameter.
     * This property allows parent components to respond to user input changes and integrate the textarea with external state management systems.
     * The function receives the standard React ChangeEvent for HTMLTextAreaElement, providing access to the new value and event details.
     */
    onChange?(e: ChangeEvent<HTMLTextAreaElement>): void;
    /**
     * Optional placeholder text that appears inside the input field when it's empty, providing hints about the expected input format.
     * This property helps guide users by showing example text or instructions directly within the textarea when no content is present.
     * The placeholder text is displayed with reduced opacity and disappears when the user begins typing or when the field has a value.
     */
    placeholder?: string;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing.
     *
     * @default 'TextAreaInput'
     */
    testId?: string;
    /**
     * Optional validation function that receives the input value and returns an error message string if validation fails, or null/undefined if valid.
     * This property enables custom validation logic to be applied to the textarea input, providing real-time feedback to users about input correctness.
     * The validation function is called during input changes and form interactions, with error messages displayed below the textarea when validation fails.
     */
    validation?: (ValidationFunc | undefined);
}

/**
 * A comprehensive textarea input component that provides rich text input functionality with validation, accessibility features, and form integration.
 * This component wraps the native HTML textarea element with enhanced styling, state management, and user interaction capabilities.
 * It supports various configurations including validation, hints, labels, and different input states while maintaining full accessibility standards.
 *
 * @param props              The component props.
 * @param props.className    Optional CSS class name for custom styling of the wrapper element.
 * @param props.hint         Optional hint text displayed below the input field to provide additional guidance or information to the user.
 * @param props.initialValue The initial value that will be displayed in the input field when the component first renders.
 * @param props.isDisabled   Determines whether the input field is disabled, preventing any user interaction or input.
 * @param props.isReadonly   Determines whether the input field is in readonly mode, allowing users to see the value but preventing modifications.
 * @param props.isRequired   Specifies whether the input field is required for form submission, typically displayed with an asterisk indicator.
 * @param props.label        Optional label text that will be displayed above the input field to identify its purpose or expected content.
 * @param props.name         The name attribute for the input field, which is essential for form data handling and submission processes.
 * @param props.onChange     Optional callback function that is triggered whenever the input value changes, receiving the change event as a parameter.
 * @param props.placeholder  Optional placeholder text that appears inside the input field when it's empty, providing hints about the expected input format.
 * @param props.testId       Unique identifier for testing purposes, crucial for uniquely identifying components during testing.
 * @param props.validation   Optional validation function that receives the input value and returns an error message string if validation fails, or null/undefined if valid.
 * @returns A React component that renders a styled textarea input with validation, labels, hints, and accessibility features.
 *
 * @example
 * ```tsx
 * <TextAreaInput
 *     name="description"
 *     label="Description"
 *     placeholder="Enter your description here..."
 *     isRequired={true}
 *     hint="Please provide at least 10 characters"
 *     validation={(value) => value.length < 10 ? "Description too short" : null}
 *     testId="description-input"
 * />
 * ```
 */
const TextAreaInput = observer(({
    className,
    hint,
    initialValue = '',
    isDisabled = false,
    isReadonly = false,
    isRequired = false,
    label,
    name,
    onChange = () => {},
    placeholder,
    testId = 'TextAreaInput',
    validation
}: ComponentProps) => {
    const {
        colors,
        error,
        fieldRef,
        formId,
        handleChange,
        value
    } = useTextAreaInput(name, initialValue, validation, onChange);

    return (
        <Wrapper className={className} data-cy={testId} variants={FadeInAnimation}>
            {label && (
                <>
                    <Label htmlFor={`${formId}-${name}`}>{label}{isRequired ? '*' : ''}</Label>
                    <Spacer y={2} />
                </>
            )}
            <Input
                ref={fieldRef}
                disabled={isDisabled}
                id={`${formId}-${name}`}
                name={name}
                placeholder={placeholder}
                readOnly={isReadonly}
                required={isRequired}
                value={value}
                onChange={handleChange}
            />
            <AnimatePresence initial={false} mode="sync">
                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                {(hint || error) && (
                    <ExtraHint animate="enter" exit="exit" initial="initial" variants={HintAnimation}>
                        <Spacer y={2} />
                        {(hint && !error) && (
                            <P>{hint}</P>
                        )}
                        {error && (
                            <P><Color $color={colors.errorColor}>{error}</Color></P>
                        )}
                    </ExtraHint>
                )}
            </AnimatePresence>
        </Wrapper>
    );
});

TextAreaInput.displayName = 'TextAreaInput';

export {TextAreaInput};

const Wrapper = styled(motion.div)`
    width: 100%;
`;

const Label = P.withComponent('label');

const Input = styled.textarea`
    background-color: ${({theme}) => theme.colors.backgroundLight};
    border: 1px solid ${({theme}) => theme.colors.darkBorder};
    border-radius: 12px;
    color: ${({theme}) => theme.colors.textPrimary};
    field-sizing: content;
    font-family: ${({theme}) => theme.fonts.Montserrat};
    font-size: 1.6rem;
    max-height: 30rem;
    min-height: 9rem;
    outline: 1px solid hsl(from ${({theme}) => theme.colors.tertiary} H S L / 0);
    padding: ${spacing(4)} ${spacing(4)};
    transition: border-color 0.2s ease-in-out, outline 0.2s ease-in-out, background-color 0.2s ease-in-out;
    transition: color 0.2s ease-in-out;
    width: 100%;

    &::placeholder {
        color: ${({theme}) => theme.colors.textNeutral};
    }

    &:user-invalid {
        background-color: hsl(from ${({theme}) => theme.colors.errorColor} H S L / 0.3);
        border-color: ${({theme}) => theme.colors.errorColor};
        color: ${({theme}) => theme.colors.errorColor};
    }

    &:disabled, &:disabled::placeholder, &:read-only, &:read-only::placeholder {
        background-color: ${({theme}) => theme.colors.textNeutral};
        border-color: ${({theme}) => theme.colors.textNeutral};
        color: ${({theme}) => theme.colors.textNeutral};
    }

    &:focus-visible {
        border-color: ${({theme}) => theme.colors.tertiary};
        outline: 1px solid hsl(from ${({theme}) => theme.colors.tertiary} H S L / 1);
    }
`;

const ExtraHint = styled(motion.div)`
    height: 0;
    overflow: hidden;

    & p {
        font-size: 1.2rem;

        ${media('md')} {
            font-size: 1.4rem;
        }
    }
`;